/**
 * CG-2 — useRelationships hook (coach-guardian phase).
 *
 * Backs the Settings "Guardians & access" section. All reads and writes go
 * through the authorized API (`/api/relationships*`, service_role side) —
 * never direct Supabase reads; the table's RLS SELECT grant exists as
 * defense-in-depth, not as a client path (its column-level grant would
 * reject `select('*')` anyway).
 *
 * Flag-driven visibility: the server reports `enabled: false` when the
 * `relationships_v1` flag is off, and the section renders nothing — no
 * client-side flag plumbing.
 *
 * Trust root (brief §3a, ratified): invites are adult-initiated by email;
 * nothing activates until the athlete accepts; either side revokes. The
 * invite response is deliberately generic (anti-enumeration) — surface its
 * copy verbatim, never "athlete not found".
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from './useAuthFetch';
import { sharedRequest } from '../lib/sharedRequest';

export interface RelationshipCounterpart {
  userId: string | null;
  name: string | null;
  email: string | null;
}

export interface Relationship {
  id: string;
  role: 'guardian' | 'clinician';
  status: 'invited' | 'active' | 'revoked';
  /** Which side of the relationship the signed-in user is. */
  mySide: 'athlete' | 'adult';
  iInvited: boolean;
  /** True when this is a pending invite waiting on MY confirmation. */
  needsMyConfirmation: boolean;
  counterpart: RelationshipCounterpart;
  createdAt: string;
  revokedAt: string | null;
}

export interface UseRelationshipsResult {
  /** Server-reported flag state — false hides the whole section. */
  enabled: boolean;
  relationships: Relationship[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Resolves with the server's generic confirmation copy. */
  invite: (athleteEmail: string) => Promise<void>;
  /** UAT-B2 — athlete-initiated guardian invite (the 13–15 beat + minor
   *  Settings). Same authorized endpoint as `invite`, opposite direction:
   *  the signed-in minor athlete invites a GUARDIAN by email. Same generic
   *  anti-enumeration response; the server records the athlete-side row. */
  inviteGuardian: (guardianEmail: string) => Promise<void>;
  accept: (id: string) => Promise<void>;
  revoke: (id: string) => Promise<void>;
}

/**
 * F-402 — coerce an API `error` field of any shape into a display string.
 * Handles a plain string, a nested `{ message }` object (Vercel edge/WAF 403:
 * `{ error: { code, message, id } }`), and anything else (falls back to a
 * status-coded generic instead of the literal "[object Object]").
 */
export function errorMessage(raw: unknown, status?: number): string {
  if (typeof raw === 'string' && raw.trim()) return raw;
  if (raw && typeof raw === 'object') {
    const msg = (raw as { message?: unknown }).message;
    if (typeof msg === 'string' && msg.trim()) return msg;
  }
  return `Request failed${status ? ` (${status})` : ''}`;
}

export function useRelationships(): UseRelationshipsResult {
  // B233 (security review, 2026-09-26) — the shared in-flight map below is
  // MODULE-level and was keyed by the bare URL, so a mount that landed
  // inside another account's in-flight GET on an in-place switch received
  // THAT account's relationships/enabled answer. Keyed by uid, the same fix
  // `useUserTier.ts` already carries (B129/B132).
  const { session, user } = useAuthContext();
  const uid = user?.id ?? null;
  const fetchAuthed = useAuthFetch();
  const [enabled, setEnabled] = useState(false);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      // De-dupe: SubscriptionSection + GuardiansSection both call this hook and
      // mount together, so share one in-flight GET per page load — keyed by
      // uid (B233) so two accounts never share one entry. An explicit
      // refetch after a mutation settles the entry first → fresh request.
      const key = uid ? `/api/relationships:${uid}` : '/api/relationships';
      const json = await sharedRequest(key, async () => {
        const res = await fetchAuthed('/api/relationships');
        if (!res.ok) throw new Error(`Failed to load relationships (${res.status})`);
        return (await res.json()) as {
          enabled?: boolean;
          relationships?: Relationship[];
        };
      });
      setEnabled(json.enabled === true);
      setRelationships(Array.isArray(json.relationships) ? json.relationships : []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [session, uid, fetchAuthed]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const action = useCallback(
    async (url: string, body?: Record<string, unknown>) => {
      const res = await fetchAuthed(url, {
        method: 'POST',
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: unknown };
      // F-402: the `error` field isn't always a string — a Vercel edge/WAF 403
      // returns `{ error: { code, message, id } }`. `new Error(object)` would
      // stringify to the literal "[object Object]" shown to the user. Coerce to
      // a real message here (the throw source) so every consumer's `err.message`
      // is clean.
      if (!res.ok) throw new Error(errorMessage(json.error, res.status));
      await refetch();
    },
    [fetchAuthed, refetch],
  );

  const invite = useCallback(
    (athleteEmail: string) =>
      action('/api/relationships/invite', { athleteEmail, role: 'guardian' }),
    [action],
  );
  const inviteGuardian = useCallback(
    (guardianEmail: string) =>
      action('/api/relationships/invite', {
        guardianEmail,
        role: 'guardian',
        asAthlete: true,
      }),
    [action],
  );
  const accept = useCallback(
    (id: string) => action(`/api/relationships/${encodeURIComponent(id)}/accept`),
    [action],
  );
  const revoke = useCallback(
    (id: string) => action(`/api/relationships/${encodeURIComponent(id)}/revoke`),
    [action],
  );

  return { enabled, relationships, loading, error, refetch, invite, inviteGuardian, accept, revoke };
}
